import React from 'react';

interface SimpleTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  editorId?: string;
}

const SimpleTextEditor: React.FC<SimpleTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Write your article content...',
  editorId = 'editor-textarea',
}) => {
  const insertFormat = (format: string) => {
    const textarea = document.getElementById(editorId) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `<strong>${selectedText || 'bold text'}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText || 'italic text'}</em>`;
        break;
      case 'h2':
        formattedText = `<h2>${selectedText || 'Heading'}</h2>`;
        break;
      case 'h3':
        formattedText = `<h3>${selectedText || 'Subheading'}</h3>`;
        break;
      case 'ul':
        formattedText = `<ul>\n  <li>${selectedText || 'Item 1'}</li>\n  <li>Item 2</li>\n</ul>`;
        break;
      case 'ol':
        formattedText = `<ol>\n  <li>${selectedText || 'Item 1'}</li>\n  <li>Item 2</li>\n</ol>`;
        break;
      case 'link':
        const url = prompt('Enter URL:', 'https://');
        if (url) {
          formattedText = `<a href="${url}" target="_blank" rel="noopener noreferrer">${selectedText || 'Link text'}</a>`;
        }
        break;
      default:
        return;
    }

    const newValue = value.substring(0, start) + formattedText + value.substring(end);
    onChange(newValue);

    // Restore focus
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + formattedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <div className="simple-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border border-gray-200 rounded-t-lg">
        <button
          type="button"
          onClick={() => insertFormat('h2')}
          className="px-3 py-1 text-sm font-semibold text-navy hover:bg-gray-200 rounded transition-colors"
          title="Heading 2"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => insertFormat('h3')}
          className="px-3 py-1 text-sm font-semibold text-navy hover:bg-gray-200 rounded transition-colors"
          title="Heading 3"
        >
          H3
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => insertFormat('bold')}
          className="px-3 py-1 text-sm font-bold text-navy hover:bg-gray-200 rounded transition-colors"
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => insertFormat('italic')}
          className="px-3 py-1 text-sm italic text-navy hover:bg-gray-200 rounded transition-colors"
          title="Italic"
        >
          I
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => insertFormat('ul')}
          className="px-3 py-1 text-sm text-navy hover:bg-gray-200 rounded transition-colors"
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => insertFormat('ol')}
          className="px-3 py-1 text-sm text-navy hover:bg-gray-200 rounded transition-colors"
          title="Numbered List"
        >
          1. List
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => insertFormat('link')}
          className="px-3 py-1 text-sm text-navy hover:bg-gray-200 rounded transition-colors"
          title="Link"
        >
          🔗 Link
        </button>
      </div>

      {/* Textarea */}
      <textarea
        id={editorId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={12}
        className="w-full px-4 py-3 border-x border-b border-gray-200 rounded-b-lg focus:ring-2 focus:ring-gold focus:border-gold font-mono text-sm leading-relaxed resize-y"
      />

      {/* HTML Preview hint */}
      <p className="text-xs text-gray-400 mt-2">
        Supports HTML tags: &lt;h2&gt;, &lt;h3&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;a&gt;
      </p>
    </div>
  );
};

export default SimpleTextEditor;
